import { memo, useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { Temporal } from '@js-temporal/polyfill';

import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import { TRequiresTimeChange } from '@/types/session.types';
import Button from '@ui/buttons/button/button';
import Popover from '@ui/popover/popover';
import { useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import getUtcOffset from '@/utils/get-utc-offset';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import OptionalComponent from '@ui/optional-component/optional-component';
import { DateRange } from '@ui/calendar/types';
import { getUserTimezone } from '@ui/calendar/utils';
import DateRangePickerHorizontal from '@ui/calendar/date-range-picker-horizontal';

/* Do not use css module for this because we need to target the css from outside of this component */
import './project-date.css';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import toastTrigger from '@ui/toast/toast-trigger';

type ProjectDateProps = {
  timestamp?: number | null;
  end_timestamp?: number | null;
  selectedTimezone?: string;
  onSave?: (data: DateRange) => void;
  onChange?: (data: DateRange) => void;
  onClose?: () => void;
  requiresTimeChange?: TRequiresTimeChange | null;
  disabled?: boolean;
  showUTCOffset?: boolean;
  isInEditMode?: boolean;
  hideLabel?: boolean;
  wrapperClass?: string;
};

const formatDate = (timestamp: number, timeZone?: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleDateString(navigator.language, { year: 'numeric', month: 'short', day: 'numeric', timeZone });
};

const formatTime = (timestamp: number, timeZone?: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString(navigator.language, { hour: 'numeric', minute: '2-digit', hour12: true, timeZone });
};

const formatDurationLabel = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

type TimeDropdownContentProps = {
  baseTimestamp: number | null | undefined;
  selectedTimestamp: number | null | undefined;
  timezone: string;
  onSelect: (timestamp: number) => void;
  minTimestamp?: number | null;
  maxTimestamp?: number | null;
  durationFromTimestamp?: number | null;
};

const SLOTS_PER_DAY = 48; // 30-min slots in a 24-hour day
const START_SLOT = 16; // slot 16 = 8:00 AM (16 * 30min = 480min)
const MINUTES_PER_SLOT = 30;
const SYSTEM_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

const TimeDropdownContent = ({
  baseTimestamp,
  selectedTimestamp,
  timezone,
  onSelect,
  minTimestamp,
  maxTimestamp,
  durationFromTimestamp,
}: TimeDropdownContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => {
    const base = baseTimestamp || Date.now();
    const datePart = Temporal.Instant.fromEpochMilliseconds(base).toZonedDateTimeISO(timezone);
    return Array.from({ length: SLOTS_PER_DAY }, (_, i) => {
      const slot = (i + START_SLOT) % SLOTS_PER_DAY;
      const hour = Math.floor(slot / 2);
      const minute = (slot % 2) * MINUTES_PER_SLOT;
      const ts = datePart
        .with({ hour, minute, second: 0, millisecond: 0, microsecond: 0, nanosecond: 0 })
        .toInstant().epochMilliseconds;
      return { ts, label: formatTime(ts, timezone) };
    });
  }, [baseTimestamp, timezone]);

  const nearestIndex = useMemo(() => {
    if (!selectedTimestamp) return 0;
    const refInTz = Temporal.Instant.fromEpochMilliseconds(selectedTimestamp).toZonedDateTimeISO(timezone);
    const slot = Math.round((refInTz.hour * 60 + refInTz.minute) / MINUTES_PER_SLOT) % SLOTS_PER_DAY;
    return (slot - START_SLOT + SLOTS_PER_DAY) % SLOTS_PER_DAY;
  }, [selectedTimestamp, timezone]);

  useEffect(() => {
    if (!selectedTimestamp) return;
    const timer = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const el = container.querySelector('[data-nearest="true"]') as HTMLElement | null;
      if (!el) return;
      const itemHeight = el.offsetHeight;
      container.scrollTop = nearestIndex * itemHeight - container.clientHeight / 2 + itemHeight / 2;
    }, 100);
    return () => clearTimeout(timer);
  }, [nearestIndex, selectedTimestamp]);

  return (
    <Container
      ref={containerRef}
      className="no-scrollbar overflow-y-scroll"
      maxHeight="160px"
      width={durationFromTimestamp ? '145px' : '100%'}
    >
      {options.map((opt, i) => {
        const isSelected = !!selectedTimestamp && Math.abs(opt.ts - selectedTimestamp) < 900000;
        const isDisabled = (!!minTimestamp && opt.ts <= minTimestamp) || (!!maxTimestamp && opt.ts >= maxTimestamp);
        const durationLabel =
          durationFromTimestamp && opt.ts > durationFromTimestamp
            ? formatDurationLabel(Math.round((opt.ts - durationFromTimestamp) / 60000))
            : null;

        return (
          <Button
            key={i}
            data-nearest={i === nearestIndex ? 'true' : undefined}
            className="clear-button width-100-percent text-left P2 padding-v-8 padding-h-16 ws-nowrap"
            backgroundColor={isSelected ? 'var(--global-cont-3)' : 'transparent'}
            hoverBackgroundColor={isDisabled ? 'transparent' : 'var(--global-cont-3)'}
            disabled={isDisabled}
            disabledOpacity={0.3}
            onClick={() => onSelect(opt.ts)}
          >
            <Container className="flex fd-row ai-center gap-12 width-100-percent">
              <Text className="P2 min-width-55" design="GlobalIcoText2">
                {opt.label}
              </Text>
              {durationLabel && (
                <Text className="C2" design="GlobalIcoText3">
                  {durationLabel}
                </Text>
              )}
            </Container>
          </Button>
        );
      })}
    </Container>
  );
};

const toDefaultTime = (timestamp: number, timezone: string): number =>
  Temporal.Instant.fromEpochMilliseconds(timestamp)
    .toZonedDateTimeISO(timezone)
    .with({
      hour: (START_SLOT * MINUTES_PER_SLOT) / 60,
      minute: 0,
      second: 0,
      millisecond: 0,
      microsecond: 0,
      nanosecond: 0,
    })
    .toInstant().epochMilliseconds;

const combineDateWithTime = (dateTimestamp: number, timeTimestamp: number, timezone: string): number => {
  const datePart = Temporal.Instant.fromEpochMilliseconds(dateTimestamp).toZonedDateTimeISO(timezone);
  const timePart = Temporal.Instant.fromEpochMilliseconds(timeTimestamp).toZonedDateTimeISO(timezone);
  return datePart
    .with({ hour: timePart.hour, minute: timePart.minute, second: 0, millisecond: 0, microsecond: 0, nanosecond: 0 })
    .toInstant().epochMilliseconds;
};

const ProjectDate = memo(
  ({
    disabled,
    timestamp,
    end_timestamp,
    onSave,
    onChange,
    requiresTimeChange,
    onClose,
    showUTCOffset,
    isInEditMode,
    selectedTimezone,
    hideLabel,
    wrapperClass,
  }: ProjectDateProps) => {
    const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

    const [dateOpen, setDateOpen] = useState(false);
    const [startTimeOpen, setStartTimeOpen] = useState(false);
    const [endTimeOpen, setEndTimeOpen] = useState(false);
    const TIMEZONE = useRef(selectedTimezone || SYSTEM_TIMEZONE);
    const prevSelectedTimezone = useRef(selectedTimezone);

    // only use server timestamps as working values when requiresTimeChange is not set —
    // if requiresTimeChange.start_date is true the server timestamp is a placeholder and
    // the user must explicitly pick a date before time pickers become available
    const [workingTimestamp, setWorkingTimestamp] = useState<number | null>(
      requiresTimeChange?.start_date ? null : timestamp || null
    );
    const workingTimestampRef = useRef(workingTimestamp);
    const [workingEndTimestamp, setWorkingEndTimestamp] = useState<number | null>(
      requiresTimeChange?.end_date ? null : end_timestamp || null
    );

    // override requiresTimeChange display once user has explicitly selected a date/time
    const [hasSelectedDate, setHasSelectedDate] = useState(false);
    const [hasSelectedStartTime, setHasSelectedStartTime] = useState(false);
    const [hasSelectedEndTime, setHasSelectedEndTime] = useState(false);

    useEffect(() => {
      workingTimestampRef.current = workingTimestamp;
    }, [workingTimestamp]);

    useEffect(() => {
      const prev = prevSelectedTimezone.current;
      prevSelectedTimezone.current = selectedTimezone;
      if (!prev || !selectedTimezone || prev === selectedTimezone || workingTimestampRef.current === null) return;
      if (selectedTimezone !== SYSTEM_TIMEZONE) {
        toastTrigger('Start time adjusted', {
          description: `The start time is now ${formatTime(workingTimestampRef.current, selectedTimezone)} due to the timezone change.`,
        });
      }
    }, [selectedTimezone]);

    useEffect(() => {
      if (timestamp && !requiresTimeChange?.start_date) {
        setWorkingTimestamp(timestamp);
        setHasSelectedDate(false);
        setHasSelectedStartTime(false);
      }
    }, [timestamp, requiresTimeChange?.start_date]);

    useEffect(() => {
      if (end_timestamp && !requiresTimeChange?.end_date) {
        setWorkingEndTimestamp(end_timestamp);
        setHasSelectedEndTime(false);
      }
    }, [end_timestamp, requiresTimeChange?.end_date]);

    useEffect(() => {
      // intentionally check for false instead of !isInEditMode
      // because we only want to control this if the prop is actually passed in
      if (isInEditMode === false) {
        setDateOpen(false);
        setStartTimeOpen(false);
        setEndTimeOpen(false);
      }
    }, [isInEditMode]);

    const handleDatePickerChange = useCallback(
      (dates: DateRange, handleClose?: () => void) => {
        if (!dates.startDate) return;
        const tz = selectedTimezone || TIMEZONE.current;
        const defaultStartTs = toDefaultTime(dates.startDate, SYSTEM_TIMEZONE);
        const newStartDate = workingTimestamp
          ? combineDateWithTime(dates.startDate, workingTimestamp, tz)
          : defaultStartTs;
        const newEndDate = workingEndTimestamp ? combineDateWithTime(dates.startDate, workingEndTimestamp, tz) : null;
        setWorkingTimestamp(newStartDate);
        setWorkingEndTimestamp(newEndDate);
        setHasSelectedDate(true);
        onChange?.({ startDate: newStartDate, endDate: newEndDate, selectedTimezone: tz });
        handleClose?.();
      },
      [onChange, workingTimestamp, workingEndTimestamp, selectedTimezone]
    );

    const getStartDate = useCallback(() => {
      const placeholderLabel = 'Set date';
      if (!workingTimestamp || (requiresTimeChange?.start_date && !hasSelectedDate)) {
        return placeholderLabel;
      }
      return formatDate(workingTimestamp, selectedTimezone || getUserTimezone());
    }, [requiresTimeChange?.start_date, workingTimestamp, selectedTimezone, hasSelectedDate]);

    const getStartTime = useCallback(() => {
      const placeholderLabel = 'Set Time';
      if (!workingTimestamp || (requiresTimeChange?.start_time && !hasSelectedStartTime)) {
        return placeholderLabel;
      }
      return formatTime(workingTimestamp, selectedTimezone || TIMEZONE.current);
    }, [requiresTimeChange?.start_time, selectedTimezone, workingTimestamp, hasSelectedStartTime]);

    const getEndTime = useCallback(() => {
      const placeholderLabel = 'Set Time';
      if (!workingEndTimestamp || (requiresTimeChange?.end_time && !hasSelectedEndTime)) {
        return placeholderLabel;
      }
      return formatTime(workingEndTimestamp, selectedTimezone || TIMEZONE.current);
    }, [workingEndTimestamp, requiresTimeChange?.end_time, selectedTimezone, hasSelectedEndTime]);

    return (
      <div data-component="ProjectDate" className={`${wrapperClass || ''}`}>
        <OptionalComponent display={!hideLabel}>
          <Text className={`L3 margin-b-8 padding-l-8`} design="GlobalIcoText3">
            DATE & TIME
          </Text>
        </OptionalComponent>
        <div className="flex fd-row jc-center ai-center gap-4 height-inherit">
          <Container design="GlobalCont1" className="flex-1 radius-8 padding-v-4 padding-r-8 padding-l-0">
            <div className="flex fd-row jc-flex-start ai-center gap-6 padding-l-12">
              <div className="flex ai-center gap-4 flex-1">
                <div className="flex-column jc-center ai-center height-42">
                  <div className="square-6 radius-full edit-project-date__startCircle" />
                  <div className="edit-project-date__line" />
                  <div className="square-6 radius-full edit-project-date__endCircle" />
                </div>

                <div className="flex-column gap-4">
                  <div className="flex ai-center height-32">
                    <Text design="GlobalIcoText3" className="L3">
                      Start
                    </Text>
                  </div>
                  <div className="flex ai-center height-32">
                    <Text design="GlobalIcoText3" className="L3">
                      End
                    </Text>
                  </div>
                </div>
              </div>
              <Popover
                disabled={disabled}
                closeOnClick={false}
                forcePosition="bottom-left"
                onClose={() => {
                  setDateOpen(false);
                  onClose?.();
                }}
                overflow="visible"
                border="none"
                verticalOffset={12}
                contentClassName="margin-t-2"
                open={!!dateOpen}
                ignoreClasses={['.react-select-menu-container']}
                containerClassName="flex-1"
                renderTriggerButton={() => (
                  <Button
                    className="clear-button flex jc-center ai-center S3 edit-project-date__startButton width-100-percent"
                    backgroundColor="transparent"
                    onKeyDown={(e) => {
                      if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                        e.preventDefault();
                        setDateOpen(true);
                      }
                    }}
                    onClick={() => {
                      setDateOpen((prev) => !prev);
                    }}
                  >
                    <Container
                      className="padding-v-6 padding-h-12 S3 text-left edit-project-date__subButton flex-column jc-flex-end gap-12 position-relative"
                      borderRadius="8px"
                      width="100%"
                      height="64px"
                      background="var(--global-cont-2)"
                      hoverBackground="var(--global-cont-3)"
                    >
                      <Icon name={ICONS.CALENDAR2} color="var(--global-ico-text-3)" />
                      <Text
                        className="S3"
                        design={
                          !workingTimestamp || (requiresTimeChange?.start_date && !hasSelectedDate)
                            ? 'GlobalIcoText3'
                            : 'GlobalIcoText2'
                        }
                      >
                        {getStartDate()}
                      </Text>
                      <Container className="position-absolute bottom-6 right-6">
                        <RotatableIcon
                          name={ICONS.SELECT_DROPDOWN_CARET}
                          size={16}
                          color="var(--global-ico-text-3)"
                          rotate={dateOpen ? '180deg' : '0deg'}
                        />
                      </Container>
                    </Container>
                  </Button>
                )}
                width="fit-content"
              >
                {({ handleClose }) => (
                  <div className="width-100-percent position-relative">
                    <DateRangePickerHorizontal
                      overflowClass="overflow-visible"
                      selectSingleDate={true}
                      hideTimes={true}
                      hideTimezone={true}
                      onChange={(dates) => handleDatePickerChange(dates, handleClose)}
                      defaultValue={{
                        startDate: workingTimestamp || null,
                        endDate: workingEndTimestamp || null,
                        selectedTimezone: selectedTimezone || TIMEZONE.current,
                      }}
                      renderFooter={({ startDate, setStartDate, setEndDate }) => {
                        if (!onSave) return null;
                        return (
                          <Container className="flex fd-row jc-flex-end ai-center padding-t-16 gap-8">
                            <Button
                              design="LocalBttnSec"
                              height="40px"
                              className="L1"
                              onClick={() => {
                                setStartDate(null);
                                setEndDate(null);
                                handleClose();
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              width="73px"
                              height="40px"
                              loading={savingWorkingProject}
                              disabled={savingWorkingProject || !startDate}
                              onClick={() => {
                                if (!startDate) return;
                                startDate.setSeconds(0);
                                const tz = selectedTimezone || TIMEZONE.current;
                                const defaultTs = toDefaultTime(startDate.getTime(), SYSTEM_TIMEZONE);
                                const newStartDate = workingTimestamp
                                  ? combineDateWithTime(startDate.getTime(), workingTimestamp, tz)
                                  : defaultTs;
                                const newEndDate = workingEndTimestamp
                                  ? combineDateWithTime(startDate.getTime(), workingEndTimestamp, tz)
                                  : null;
                                onSave({
                                  startDate: newStartDate,
                                  endDate: newEndDate,
                                  selectedTimezone: tz,
                                });
                                handleClose();
                              }}
                              className="flex ai-center"
                              hoverOpacity={0.8}
                              backgroundColor={getThemeColorCSSVariable()}
                            >
                              <Text
                                color={getThemeColorContrastCSSVariable(
                                  EColorVariablesV2.buttonText,
                                  EColorVariablesV2.primary
                                )}
                                className="L1"
                              >
                                Save
                              </Text>
                            </Button>
                          </Container>
                        );
                      }}
                    />
                  </div>
                )}
              </Popover>

              <div className="flex-column gap-4 flex-1">
                <div className="flex fd-row jc-flex-start ai-center width-100-percent">
                  <Popover
                    disabled={disabled}
                    closeOnClick={false}
                    onClose={() => setStartTimeOpen(false)}
                    overflow="visible"
                    border="none"
                    popoverWidth="fit-content"
                    contentClassName="margin-t-2"
                    containerClassName="width-100-percent"
                    open={!!startTimeOpen}
                    cycleWithArrowKeys
                    ignoreClasses={['.react-datepicker-time__input']}
                    renderTriggerButton={() => (
                      <Button
                        className="clear-button flex fd-row jc-center ai-center gap-4 S3 edit-project-date__startButton width-100-percent"
                        backgroundColor="var(--global-cont-2)"
                        borderRadius="8px"
                        disabledOpacity={0.6}
                        hoverBackgroundColor="var(--global-cont-3)"
                        onClick={() => setStartTimeOpen((prev) => !prev)}
                        onKeyDown={(e) => {
                          if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                            e.preventDefault();
                            setStartTimeOpen(true);
                          }
                        }}
                      >
                        <Container
                          className="padding-v-6 padding-h-12 S3 edit-project-date__subButton position-relative"
                          background="transparent"
                          width="100%"
                          height="30px"
                        >
                          <Text
                            className="S3"
                            design={
                              !workingTimestamp || (requiresTimeChange?.start_time && !hasSelectedStartTime)
                                ? 'GlobalIcoText3'
                                : 'GlobalIcoText2'
                            }
                          >
                            {getStartTime()}
                          </Text>
                          <Container className="position-absolute bottom-6 right-6">
                            <RotatableIcon
                              name={ICONS.SELECT_DROPDOWN_CARET}
                              size={16}
                              color="var(--global-ico-text-3)"
                              rotate={startTimeOpen ? '180deg' : '0deg'}
                            />
                          </Container>
                        </Container>
                      </Button>
                    )}
                  >
                    {({ handleClose }) => {
                      const tz = selectedTimezone || TIMEZONE.current;
                      return (
                        <Container design="GlobalCont2" borderRadius="12px" className="overflow-hidden">
                          <TimeDropdownContent
                            baseTimestamp={workingTimestamp}
                            selectedTimestamp={workingTimestamp}
                            timezone={tz}
                            maxTimestamp={workingEndTimestamp}
                            onSelect={(ts) => {
                              const newEnd = workingEndTimestamp ?? ts + 3600000;
                              setWorkingTimestamp(ts);
                              setWorkingEndTimestamp(newEnd);
                              setHasSelectedStartTime(true);
                              onChange?.({ startDate: ts, endDate: newEnd, selectedTimezone: tz });
                              handleClose();
                            }}
                          />
                        </Container>
                      );
                    }}
                  </Popover>
                </div>

                <div className="flex fd-row jc-flex-start ai-center width-100-percent">
                  <Popover
                    disabled={disabled}
                    closeOnClick={false}
                    forcePosition="bottom-right"
                    onClose={() => setEndTimeOpen(false)}
                    overflow="visible"
                    border="none"
                    popoverWidth="fit-content"
                    contentClassName="margin-t-2"
                    containerClassName="width-100-percent"
                    open={!!endTimeOpen}
                    ignoreClasses={['.react-datepicker-time__input']}
                    cycleWithArrowKeys
                    renderTriggerButton={() => (
                      <Button
                        className="clear-button flex fd-row jc-center ai-center gap-4 S3 edit-project-date__endButton width-100-percent"
                        backgroundColor="var(--global-cont-2)"
                        borderRadius="8px"
                        disabledOpacity={0.6}
                        hoverBackgroundColor="var(--global-cont-3)"
                        onClick={() => setEndTimeOpen((prev) => !prev)}
                        onKeyDown={(e) => {
                          if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                            e.preventDefault();
                            setEndTimeOpen(true);
                          }
                        }}
                      >
                        <Container
                          className="padding-v-6 padding-h-12 S3 edit-project-date__subButton position-relative"
                          background="transparent"
                          width="100%"
                          height="30px"
                        >
                          <Text
                            className="S3"
                            design={
                              !workingEndTimestamp || (requiresTimeChange?.end_time && !hasSelectedEndTime)
                                ? 'GlobalIcoText3'
                                : 'GlobalIcoText2'
                            }
                          >
                            {getEndTime()}
                          </Text>
                          <Container className="position-absolute bottom-6 right-6">
                            <RotatableIcon
                              name={ICONS.SELECT_DROPDOWN_CARET}
                              size={16}
                              color="var(--global-ico-text-3)"
                              rotate={endTimeOpen ? '180deg' : '0deg'}
                            />
                          </Container>
                        </Container>
                      </Button>
                    )}
                  >
                    {({ handleClose }) => {
                      const tz = selectedTimezone || TIMEZONE.current;
                      return (
                        <Container design="GlobalCont2" borderRadius="12px" className="overflow-hidden">
                          <TimeDropdownContent
                            baseTimestamp={workingTimestamp}
                            selectedTimestamp={workingEndTimestamp}
                            timezone={tz}
                            minTimestamp={workingTimestamp}
                            durationFromTimestamp={workingTimestamp}
                            onSelect={(ts) => {
                              const newStart = workingTimestamp ?? ts - 3600000;
                              setWorkingEndTimestamp(ts);
                              setWorkingTimestamp(newStart);
                              setHasSelectedEndTime(true);
                              onChange?.({ startDate: newStart, endDate: ts, selectedTimezone: tz });
                              handleClose();
                            }}
                          />
                        </Container>
                      );
                    }}
                  </Popover>
                </div>
              </div>
            </div>
          </Container>

          <OptionalComponent display={showUTCOffset}>
            <Container
              design="GlobalCont1"
              className="flex-column radius-12 padding-v-12 padding-h-16"
              height="78px"
              width="112px"
            >
              <Icon name={ICONS.GLOBE_OUTLINE} size={16} color="var(--global-ico-text-2)" />
              <Text className="S2 margin-t-6 margin-b-2">{getUtcOffset(timestamp || Date.now())}</Text>

              <TooltipPortal
                tooltipContent={
                  <Text color="var(--gray-true-dark-100)" className="C2">
                    {TIMEZONE.current}
                  </Text>
                }
                tailSize={0}
                delayStart={150}
                animationType="slide-up"
                keepOpenOnContentHover={false}
                verticalOffset={-8}
              >
                <Text clamp={1} design="GlobalIcoText3" className="C1">
                  {TIMEZONE.current}
                </Text>
              </TooltipPortal>
            </Container>
          </OptionalComponent>
        </div>
      </div>
    );
  }
);

export default ProjectDate;
